import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { navy:'#0D1B2A', teal:'#00B4D8', success:'#10B981', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', amber:'#F59E0B' };

const STATUS_CONF = {
  not_started: { label:'Not Started', color:'#6B7280', bg:'#F3F4F6' },
  in_progress:  { label:'In Progress', color:'#D97706', bg:'#FEF3C7' },
  completed:    { label:'Completed',   color:'#059669', bg:'#D1FAE5' },
};

const CEFR_COLOR = { A1:'#6B7280', A2:'#64748B', B1:'#3B82F6', B2:'#6366F1', C1:'#8B5CF6', C2:'#EC4899' };

export default function AdminAssessment() {
  const [tab, setTab] = useState('slots');
  const [slots, setSlots] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [slotBookings, setSlotBookings] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date:'', time:'09:00', capacity:5, location:'', notes:'' });
  const [saving, setSaving] = useState(false);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/api/admin/assessment-slots').catch(() => ({ data: { slots: [] } })),
      api.get('/api/admin/assessment-overview').catch(() => ({ data: { students: [] } })),
    ]).then(([sr, or]) => {
      setSlots(Array.isArray(sr.data) ? sr.data : (sr.data?.slots || []));
      setStudents(Array.isArray(or.data) ? or.data : (or.data?.students || []));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const loadBookings = async (slotId) => {
    if (slotBookings[slotId]) return;
    try {
      const r = await api.get(`/api/admin/assessment-slots/${slotId}/bookings`);
      setSlotBookings(prev => ({ ...prev, [slotId]: r.data.bookings || [] }));
    } catch {}
  };

  const toggleSlot = (id) => {
    if (expandedSlot === id) { setExpandedSlot(null); return; }
    setExpandedSlot(id);
    loadBookings(id);
  };

  const createSlot = async () => {
    if (!form.date || !form.time) { toast.error('Date and time required'); return; }
    setSaving(true);
    try {
      await api.post('/api/admin/assessment-slots', form);
      toast.success('Slot created');
      setShowForm(false);
      setForm({ date:'', time:'09:00', capacity:5, location:'', notes:'' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const deleteSlot = async (id) => {
    if (!window.confirm('Delete this slot?')) return;
    try {
      await api.delete(`/api/admin/assessment-slots/${id}`);
      toast.success('Slot deleted');
      fetchAll();
    } catch { toast.error('Failed to delete'); }
  };

  const markAttended = async (slotId, bookingId, attended) => {
    await api.put(`/api/admin/assessment-slots/${slotId}/bookings/${bookingId}/attended`, { attended });
    setSlotBookings(prev => ({
      ...prev,
      [slotId]: (prev[slotId] || []).map(b => b.id === bookingId ? { ...b, attended } : b),
    }));
  };

  const resendAssessment = async (slotId, bookingId) => {
    try {
      const r = await api.post(`/api/admin/assessment-slots/${slotId}/bookings/${bookingId}/resend`);
      toast.success(r.data?.message || 'Confirmation re-sent');
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to resend'); }
  };

  const getAssessmentLogin = async (slotId, bookingId) => {
    try {
      const { data: d } = await api.post(`/api/admin/assessment-slots/${slotId}/bookings/${bookingId}/login`);
      if (d.is_candidate) {
        window.alert('Candidate login — give these to the student:\n\n' +
          'Login page:  ' + d.login_url + '\n' +
          'Email:       ' + d.email + '\n' +
          'Password:    ' + d.temp_password + '\n\n' +
          '(A fresh password was just generated for them.)');
      } else {
        window.alert(d.note + '\n\nEmail: ' + d.email + '\nLogin page: ' + d.login_url);
      }
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to get login'); }
  };

  const [bookForm, setBookForm] = useState({});
  const [booking, setBooking] = useState(false);
  const bookStudent = async (slotId) => {
    const f = bookForm[slotId] || {};
    if (!f.name || !f.email) { toast.error('Enter the candidate name and email'); return; }
    setBooking(true);
    try {
      const r = await api.post(`/api/admin/assessment-slots/${slotId}/book`, { name: f.name, email: f.email });
      toast.success('Booked into assessment');
      if (r.data && r.data.login) {
        window.alert(
          'Assessment login created — give these to the candidate:\n\n' +
          'Login page:  ' + r.data.login.login_url + '\n' +
          'Email:       ' + r.data.login.email + '\n' +
          'Password:    ' + r.data.login.temp_password + '\n\n' +
          'They log in and the Free Assessment starts automatically. (Separate from course enrolment.)'
        );
      }
      setBookForm(prev => ({ ...prev, [slotId]: { name: '', email: '' } }));
      setSlotBookings(prev => { const c = { ...prev }; delete c[slotId]; return c; });
      loadBookings(slotId);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to book'); }
    setBooking(false);
  };

  const removeStudent = async (id, name) => {
    if (!window.confirm(`Remove ${name}? Their account will be archived (recoverable for 30 days) and they'll disappear from this list.`)) return;
    try {
      await api.delete('/api/admin/students/' + id, { data: { reason: 'Removed via Assessment overview' } });
      toast.success('Student removed');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to remove'); }
  };

  const stats = {
    total: students.length,
    not_started: students.filter(s => s.assessment_status === 'not_started').length,
    in_progress:  students.filter(s => s.assessment_status === 'in_progress').length,
    completed:    students.filter(s => s.assessment_status === 'completed').length,
  };

  const inp = { padding:'10px 12px', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:14, outline:'none', background:'#fff' };

  return (
    <Layout role="admin">
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom:24 }}>
          <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:800, color:C.navy }}>Assessment Centre</h2>
          <p style={{ margin:0, fontSize:14, color:C.grey }}>Manage assessment slots and track student progress</p>
        </div>

        {/* Summary stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:24 }}>
          {[
            { label:'Total Students', value: stats.total,       color: C.navy },
            { label:'Not Started',    value: stats.not_started, color: C.grey },
            { label:'In Progress',    value: stats.in_progress, color: C.amber },
            { label:'Completed',      value: stats.completed,   color: C.success },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius:12, padding:'14px 16px', border:`1px solid ${C.border}` }}>
              <div style={{ fontSize:24, fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:24, background:'#F1F5F9', borderRadius:12, padding:4 }}>
          {[['slots','📅 Assessment Slots'],['students','🎓 Student Overview']].map(([key,label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ flex:1, padding:'10px 0', border:'none', borderRadius:9, fontWeight:700, fontSize:14, cursor:'pointer',
                       background: tab===key ? '#fff' : 'transparent', color: tab===key ? C.navy : C.grey,
                       boxShadow: tab===key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none' }}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading…</div>
        ) : tab === 'slots' ? (

          /* ── SLOTS TAB ── */
          <>
            <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
              <button onClick={() => setShowForm(s => !s)}
                style={{ padding:'10px 20px', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                {showForm ? 'Cancel' : '+ Add Assessment Slot'}
              </button>
            </div>

            {showForm && (
              <div style={{ background:'#fff', borderRadius:14, padding:24, border:`1.5px solid ${C.teal}`, marginBottom:20 }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.navy, marginBottom:16 }}>📅 New Assessment Slot</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap:12, marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Date *</div>
                    <input type="date" style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                      value={form.date} onChange={e => setForm(f => ({...f, date:e.target.value}))} />
                  </div>
                  <div>
                    <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Time *</div>
                    <input type="time" style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                      value={form.time} onChange={e => setForm(f => ({...f, time:e.target.value}))} />
                  </div>
                  <div>
                    <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Capacity</div>
                    <input type="number" min={1} max={30} style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                      value={form.capacity} onChange={e => setForm(f => ({...f, capacity:parseInt(e.target.value)||1}))} />
                  </div>
                </div>
                <div style={{ marginBottom:12 }}>
                  <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Room / Location (optional)</div>
                  <input type="text" placeholder="e.g. Room 3, Ground Floor" style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                    value={form.location} onChange={e => setForm(f => ({...f, location:e.target.value}))} />
                </div>
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:12, color:C.grey, marginBottom:4 }}>Notes (optional)</div>
                  <input type="text" placeholder="e.g. Bring headphones" style={{ ...inp, width:'100%', boxSizing:'border-box' }}
                    value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} />
                </div>
                <div style={{ background:'#FFF7ED', border:'1px solid #FED7AA', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400E', marginBottom:16 }}>
                  ⏱ Assessment duration: approx <strong>75 minutes</strong>. Allow 90 minutes per slot. Students sit a 5-section AI test (Grammar, Vocabulary, Reading, Listening, Writing).
                </div>
                <button onClick={createSlot} disabled={saving}
                  style={{ padding:'10px 24px', background:saving?C.border:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:saving?'not-allowed':'pointer' }}>
                  {saving ? 'Creating…' : 'Create Slot'}
                </button>
              </div>
            )}

            {slots.length === 0 ? (
              <div style={{ background:'#fff', borderRadius:14, padding:48, textAlign:'center' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>📅</div>
                <div style={{ fontWeight:700, fontSize:16, color:C.navy, marginBottom:6 }}>No assessment slots yet</div>
                <div style={{ fontSize:14, color:C.grey }}>Add a slot so students can book an in-person assessment time.</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {slots.map(s => (
                  <div key={s.id} style={{ background:'#fff', borderRadius:14, border:`1px solid ${C.border}`, overflow:'hidden' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'16px 20px', cursor:'pointer' }}
                      onClick={() => toggleSlot(s.id)}>
                      <div style={{ width:48, height:48, borderRadius:10, background:C.bg, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <div style={{ fontSize:11, fontWeight:800, color:C.teal, lineHeight:1 }}>
                          {s.date ? new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { month:'short' }).toUpperCase() : ''}
                        </div>
                        <div style={{ fontSize:18, fontWeight:800, color:C.navy, lineHeight:1 }}>
                          {s.date ? new Date(s.date + 'T00:00:00').getDate() : ''}
                        </div>
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>
                          {s.date ? new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long' }) : s.date} at {(s.time || '').slice(0,5)}
                        </div>
                        <div style={{ fontSize:12, color:C.grey }}>
                          {s.location ? `📍 ${s.location} · ` : ''}{(s.booked ?? s.booked_count ?? 0)}/{s.capacity} booked
                          {s.notes ? ` · ${s.notes}` : ''}
                        </div>
                      </div>
                      {(() => { const left = Math.max(0, (s.capacity || 0) - (s.booked ?? s.booked_count ?? 0)); return (
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{
                          padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700,
                          background: left === 0 ? '#FEE2E2' : '#D1FAE5',
                          color: left === 0 ? C.error : C.success,
                        }}>
                          {left === 0 ? 'Full' : `${left} space${left !== 1 ? 's' : ''} left`}
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteSlot(s.id); }}
                          style={{ padding:'6px 10px', background:'none', border:`1px solid ${C.border}`, borderRadius:8, color:C.error, fontSize:12, cursor:'pointer' }}>
                          Delete
                        </button>
                        <span style={{ color:C.grey }}>{expandedSlot===s.id?'▲':'▼'}</span>
                      </div>
                      ); })()}
                    </div>

                    {expandedSlot === s.id && (
                      <div style={{ borderTop:`1px solid ${C.border}`, padding:'14px 20px', background:'#F9FAFB' }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.grey, textTransform:'uppercase', marginBottom:10 }}>
                          Booked Students ({(slotBookings[s.id]||[]).length})
                        </div>
                        {!(slotBookings[s.id]||[]).length ? (
                          <div style={{ fontSize:13, color:C.grey, fontStyle:'italic' }}>No bookings yet</div>
                        ) : (
                          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                            {(slotBookings[s.id]||[]).map(b => (
                              <div key={b.id} style={{ display:'flex', alignItems:'center', gap:12, fontSize:13 }}>
                                <span style={{ width:28, height:28, borderRadius:'50%', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:C.teal, fontSize:12 }}>
                                  {b.name[0]}
                                </span>
                                <div style={{ flex:1 }}>
                                  <div style={{ fontWeight:600, color:C.navy }}>{b.name}</div>
                                  <div style={{ color:C.grey, fontSize:11 }}>{b.email}</div>
                                </div>
                                <button onClick={() => markAttended(s.id, b.id, !b.attended)}
                                  style={{ padding:'4px 12px', border:`1.5px solid ${b.attended?C.success:C.border}`,
                                    borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer',
                                    background: b.attended?'#D1FAE5':'#fff', color: b.attended?C.success:C.grey }}>
                                  {b.attended ? '✓ Checked in — can start' : '✓ Check in & start'}
                                </button>
                                <button onClick={() => resendAssessment(s.id, b.id)} title="Resend booking confirmation email"
                                  style={{ padding:'4px 10px', border:`1.5px solid ${C.border}`, borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', background:'#fff', color:C.grey }}>
                                  ✉ Resend
                                </button>
                                <button onClick={() => getAssessmentLogin(s.id, b.id)} title="Show login to hand to the candidate"
                                  style={{ padding:'4px 10px', border:`1.5px solid ${C.teal}`, borderRadius:8, fontWeight:700, fontSize:12, cursor:'pointer', background:'#fff', color:C.teal }}>
                                  🔑 Login
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{ borderTop:`1px dashed ${C.border}`, marginTop:14, paddingTop:14 }}>
                          <div style={{ fontSize:12, fontWeight:700, color:C.grey, textTransform:'uppercase', marginBottom:8 }}>Book a candidate</div>
                          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                            <input placeholder="Full name" value={(bookForm[s.id]||{}).name || ''}
                              onChange={e => setBookForm(p => ({ ...p, [s.id]: { ...(p[s.id]||{}), name: e.target.value } }))}
                              style={{ ...inp, flex:'1 1 140px' }} />
                            <input placeholder="Email" value={(bookForm[s.id]||{}).email || ''}
                              onChange={e => setBookForm(p => ({ ...p, [s.id]: { ...(p[s.id]||{}), email: e.target.value } }))}
                              style={{ ...inp, flex:'1 1 180px' }} />
                            <button onClick={() => bookStudent(s.id)} disabled={booking}
                              style={{ padding:'9px 16px', borderRadius:8, background:C.teal, color:'#fff', border:'none', fontWeight:700, fontSize:13, cursor:'pointer' }}>
                              {booking ? 'Booking…' : '+ Book'}
                            </button>
                          </div>
                          <div style={{ fontSize:11, color:C.grey, marginTop:6 }}>
                            New email → a separate assessment login is created and shown to hand over. Existing student email → books that student.
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>

        ) : (

          /* ── STUDENT OVERVIEW TAB ── */
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {students.length === 0 ? (
              <div style={{ background:'#fff', borderRadius:14, padding:48, textAlign:'center' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🎓</div>
                <div style={{ fontWeight:700, fontSize:16, color:C.navy }}>No students yet</div>
              </div>
            ) : students.map(s => {
              const sc = STATUS_CONF[s.assessment_status] || STATUS_CONF.not_started;
              return (
                <div key={s.student_id} style={{ background:'#fff', borderRadius:12, border:`1px solid ${C.border}`, padding:'14px 20px', display:'flex', alignItems:'center', gap:14 }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:C.bg, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:15, color:C.teal }}>
                    {(s.name||'S')[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{s.name}</div>
                    <div style={{ fontSize:12, color:C.grey }}>
                      {s.email} · Registered {s.registered}
                      {s.slot_date ? ` · Booked: ${s.slot_date} ${s.slot_time}` : ''}
                    </div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                    {s.overall_cefr && (
                      <div style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:800,
                        background:`${CEFR_COLOR[s.overall_cefr]}20`, color:CEFR_COLOR[s.overall_cefr] }}>
                        {s.overall_cefr}
                      </div>
                    )}
                    <div style={{ padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:700,
                      background: sc.bg, color: sc.color }}>
                      {sc.label}
                    </div>
                    <button onClick={() => removeStudent(s.student_id, s.name)} title="Archive this student (recoverable 30 days)"
                      style={{ padding:'4px 10px', borderRadius:8, border:`1px solid ${C.border}`, background:'#fff', color:C.error, cursor:'pointer', fontSize:12, fontWeight:700 }}>
                      🗑 Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
